import { type ClientError, makeClient } from '@studoku/shared';

/**
 * Obtain a proof-of-work token, or null if one can't be produced. Exported so
 * the contract with `@cap.js/widget` is covered directly: the widget's types
 * are the only thing checking the config we pass and the shape we read back.
 */
export async function solveProofOfWork(): Promise<string | null> {
    try {
        console.log('[solveProofOfWork] Solving Proof of Work');
        // Import Cap widget dynamically
        const { default: Cap } = await import('@cap.js/widget');

        // Initialize Cap widget in invisible mode. Worker count is configured
        // through the `data-cap-worker-count` attribute (a string); the
        // previous `workers` option was never part of CapConfig and was
        // silently ignored by the library.
        const cap = new Cap({
            apiEndpoint: '/api/proof-of-work/',
            'data-cap-worker-count': String(navigator.hardwareConcurrency || 8),
        });

        // Solve the Proof of Work challenge
        const solution = await cap.solve();
        if (!solution.success) {
            console.error('[solveProofOfWork] Proof of Work reported failure');
            return null;
        }

        console.log('[solveProofOfWork] Proof of Work solved');
        return solution.token;
    } catch (error) {
        console.error(
            '[solveProofOfWork] Proof of Work solving failed:',
            error,
        );
        return null;
    }
}

/**
 * Deploy-handshake handlers, injected by the store at startup (see
 * `useAppStore().installHandshake()`). Kept as a mutable registry rather than
 * importing the store here so `http.ts` and `store.ts` don't form an
 * eval-time import cycle. Handlers stay optional: until the store registers
 * them these are no-ops, and API-test usage of `makeClient` passes no handlers
 * at all, so its behavior is unchanged.
 */
interface HandshakeHandlers {
    onMaintenance: () => void;
    onVersionRequired: () => void;
    onVersionChange: (serverVersion: string) => void;
}

let handshake: Partial<HandshakeHandlers> = {};

export function installHandshakeHandlers(handlers: HandshakeHandlers): void {
    handshake = handlers;
}

/**
 * An uninjected id must be `undefined`, or skew pins the banner open.
 */
export function resolveBuildId(raw: string | undefined): string | undefined {
    const trimmed = raw?.trim();
    return trimmed ? trimmed : undefined;
}

const client = makeClient({
    solveProofOfWork,
    buildId: resolveBuildId(import.meta.env.VITE_STUDOKU_VERSION),
    onMaintenance: () => handshake.onMaintenance?.(),
    onVersionRequired: () => handshake.onVersionRequired?.(),
    onVersionChange: (serverVersion) =>
        handshake.onVersionChange?.(serverVersion),
});

/**
 * Predicate telling the guard whether mutating requests are currently blocked.
 * Registered by the store at startup (see `useAppStore().installHandshake()`),
 * which wires it to the `mutationsBlocked` getter. Kept as a mutable registry
 * — rather than importing the store here — for the same reason as the handshake
 * handlers above: so `http.ts` and `store.ts` never form an eval-time import
 * cycle. Until the store registers it, mutations are never blocked (the default
 * matches the store's initial non-maintenance state).
 */
let mutationsBlocked: () => boolean = () => false;

export function installMutationGuard(predicate: () => boolean): void {
    mutationsBlocked = predicate;
}

// Contract keys are `"<METHOD> <path>"`; these verbs are the mutating writes.
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Blocking reads too would strand the poll that detects window-end.
 */
export const http = ((key: string, ...args: unknown[]) => {
    const method = key.slice(0, key.indexOf(' '));
    if (MUTATING_METHODS.has(method) && mutationsBlocked()) {
        const blocked: ClientError = {
            success: false,
            errorType: 'client',
            message: 'Studoku is temporarily unavailable for maintenance',
            details: { maintenance: true },
        };
        return Promise.resolve(blocked);
    }
    return (client as (...a: unknown[]) => unknown)(key, ...args);
}) as unknown as typeof client;
